import type { WeatherSnapshot } from '../types';

const WEATHER_KEY = 'jakcomfort:weather';
const TTL_MS = 20 * 60 * 1000;

const demo: WeatherSnapshot = {
  apparentTemperature: 29,
  humidity: 68,
  precipitationProbability: 18,
  windSpeed: 9,
  weatherCode: 1,
  usAqi: 54,
  pm25: 16,
  fetchedAt: new Date().toISOString(),
  source: 'demo',
};

function readCache(): WeatherSnapshot | null {
  try {
    const raw = localStorage.getItem(WEATHER_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as WeatherSnapshot;
    const age = Date.now() - new Date(value.fetchedAt).getTime();
    if (age > TTL_MS) return null;
    return { ...value, source: 'cache' };
  } catch {
    return null;
  }
}

export async function getJakartaWeather(
  latitude = -6.1754,
  longitude = 106.8272,
): Promise<WeatherSnapshot> {
  const cached = readCache();
  if (cached) return cached;

  try {
    const weatherUrl = new URL('https://api.open-meteo.com/v1/forecast');
    weatherUrl.searchParams.set('latitude', String(latitude));
    weatherUrl.searchParams.set('longitude', String(longitude));
    weatherUrl.searchParams.set('current', 'apparent_temperature,relative_humidity_2m,wind_speed_10m,weather_code');
    weatherUrl.searchParams.set('hourly', 'precipitation_probability');
    weatherUrl.searchParams.set('forecast_days', '1');
    weatherUrl.searchParams.set('timezone', 'Asia/Jakarta');

    const airUrl = new URL('https://air-quality-api.open-meteo.com/v1/air-quality');
    airUrl.searchParams.set('latitude', String(latitude));
    airUrl.searchParams.set('longitude', String(longitude));
    airUrl.searchParams.set('current', 'us_aqi,pm2_5');
    airUrl.searchParams.set('timezone', 'Asia/Jakarta');

    const [weatherResponse, airResponse] = await Promise.all([fetch(weatherUrl), fetch(airUrl)]);
    if (!weatherResponse.ok || !airResponse.ok) throw new Error('API lingkungan tidak tersedia');

    const weather = await weatherResponse.json() as {
      current: {
        apparent_temperature: number;
        relative_humidity_2m: number;
        wind_speed_10m: number;
        weather_code: number;
        time: string;
      };
      hourly: { time: string[]; precipitation_probability: number[] };
    };
    const air = await airResponse.json() as { current: { us_aqi: number; pm2_5: number } };
    const index = Math.max(0, weather.hourly.time.indexOf(weather.current.time));

    const value: WeatherSnapshot = {
      apparentTemperature: Math.round(weather.current.apparent_temperature),
      humidity: Math.round(weather.current.relative_humidity_2m),
      precipitationProbability: Math.round(weather.hourly.precipitation_probability[index] ?? 20),
      windSpeed: Math.round(weather.current.wind_speed_10m),
      weatherCode: weather.current.weather_code,
      usAqi: Math.round(air.current.us_aqi),
      pm25: Math.round(air.current.pm2_5),
      fetchedAt: new Date().toISOString(),
      source: 'live',
    };
    localStorage.setItem(WEATHER_KEY, JSON.stringify(value));
    return value;
  } catch {
    return { ...demo, fetchedAt: new Date().toISOString() };
  }
}
